import { InformationCircleIcon, XMarkIcon } from '@heroicons/react/24/solid';
import { yupResolver } from '@hookform/resolvers/yup';
import { Modal, Tooltip } from '@kira/checkpoint-shared-frontend';
import { useQueryClient } from '@tanstack/react-query';
import React, { useContext, useMemo, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import * as yup from 'yup';

import Button, { ButtonVariant } from 'components/Button/Button';
import RadioButton from 'components/RadioButton/RadioButton';
import TextArea from 'components/TextArea/TextArea';
import useCreateCompetency from 'hooks/useCreateCompetency';
import useNotification from 'hooks/useNotification';
import { t } from 'locales/i18n';
import { AssessmentDetailsContext } from 'providers/AssessmentDetailsProvider';

enum FormFields {
  NAME = 'name',
}

interface FormData {
  [FormFields.NAME]: string;
}
interface CreateCompetencyModalProps {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  isLoading?: boolean;
}

const CreateCompetencyModal = ({
  isOpen,
  setIsOpen,
}: CreateCompetencyModalProps) => {
  const competencyTypes = [
    { id: 1, name: 'Questions', value: 'questions' },
    { id: 2, name: 'Passive', value: 'passive' },
  ];
  const [competencyType, setCompetencyType] = useState<string>(
    competencyTypes[0].value
  );
  const { interviewUid } = useParams();
  const navigate = useNavigate();
  const { showSuccessNotification, showErrorNotification } = useNotification();
  const queryClient = useQueryClient();
  const { createCompetency, isLoading } = useCreateCompetency();
  const { isPractice } = useContext(AssessmentDetailsContext);

  const FormSchema = useMemo(
    () =>
      yup.object().shape({
        [FormFields.NAME]: yup
          .string()
          .required(t('Name is required'))
          .max(50, t('Competency name cannot exceed 50 characters')),
      }),
    [FormFields.NAME]
  );

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty },
    watch,
    reset,
  } = useForm<FormData>({
    resolver: yupResolver(FormSchema),
    mode: 'onSubmit',
    defaultValues: {
      [FormFields.NAME]: '',
    },
  });

  const onSubmit: SubmitHandler<FormData> = async ({ name }) => {
    if (!interviewUid) return;

    await createCompetency(
      {
        name,
        type: competencyType,
        interview: interviewUid,
        createWithRubric: true,
      },
      {
        onSuccess: ({ uid: competencyUid }) => {
          showSuccessNotification(
            t('Success'),
            isPractice
              ? t(`Question group ${name} created`)
              : t(`Competency ${name} created`)
          );
          queryClient.invalidateQueries({
            queryKey: ['getAssessmentStepDetails', interviewUid],
          });

          navigate(
            `/interview/${interviewUid}/steps/competency/${competencyUid}`
          );
        },
        onError: (error) => {
          showErrorNotification(t('Error'), error.message);
        },
      }
    );

    setIsOpen(false);
    reset();
  };

  const toolTipText = t(
    'Question competencies use written and video questions to assess applicants. Passive competencies assess applicants on their overall performance without questions.'
  );

  return (
    <Modal
      isOpen={isOpen}
      setOpen={setIsOpen}
      showOverlay
      childContainerClassName="overflow-visible !min-h-full"
    >
      <h3 className="!prose-title-h3 flex justify-between leading-6 text-kira-slate-900">
        {isPractice ? t('Question group details') : t('Competency details')}
        <XMarkIcon
          onClick={() => setIsOpen(false)}
          className="h-7 w-7 cursor-pointer p-1 hover:rounded-full hover:bg-kira-slate-100"
        />
      </h3>
      <form
        data-testid="create-assessment-form"
        onSubmit={handleSubmit(onSubmit)}
      >
        <fieldset>
          <div className="flex flex-col">
            <div className="flex flex-col md:flex-row">
              <div className="flex-1">
                <div className="mt-4">
                  <TextArea
                    label={t('Name')}
                    id={`competency-${FormFields.NAME}`}
                    value={watch(FormFields.NAME)}
                    errors={errors[FormFields.NAME]}
                    dataTestId="competencyNameInput"
                    errorDataTestId="competencyNameError"
                    {...register(FormFields.NAME)}
                  />
                  {!isPractice && (
                    <fieldset>
                      <div className="mb-2">
                        <div className="!prose-title-input flex text-kira-slate-700">
                          {t('Type')}
                          <Tooltip
                            textContent={toolTipText}
                            tooltipClassNames="z-50 left-0"
                            textClassNames="text-left whitespace-pre-line"
                          >
                            <InformationCircleIcon className="ml-1 inline-block h-3 w-3 text-kira-slate-700" />
                          </Tooltip>
                        </div>
                      </div>
                      <div className="space-y-4 sm:flex sm:items-center sm:space-x-10 sm:space-y-0">
                        {competencyTypes.map(({ id, name, value }, index) => (
                          <RadioButton
                            dataTestId={name}
                            key={id}
                            index={index}
                            id={id.toString()}
                            checked={value === competencyType}
                            name={name}
                            onChange={() => setCompetencyType(value)}
                          />
                        ))}
                      </div>
                    </fieldset>
                  )}
                </div>
              </div>
            </div>
            <div className="flex justify-end space-x-2 pt-6">
              <Button
                variant={ButtonVariant.CANCEL}
                onClick={() => setIsOpen(false)}
                disabled={isLoading}
                buttonText={t('Cancel')}
                dataTestId="cancel"
              />
              <Button
                variant={ButtonVariant.PRIMARY}
                type="submit"
                dataTestId="submit"
                disabled={isLoading || !isDirty}
                buttonText={
                  isPractice
                    ? t('Create question group')
                    : t('Create competency')
                }
              />
            </div>
          </div>
        </fieldset>
      </form>
    </Modal>
  );
};

export default CreateCompetencyModal;
